import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import ImagePreviewModal from './ImagePreviewModal';
import ConfirmModal from './ConfirmModal';
import '../../styles/MeasureGuideModal.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const MeasureGuideModal = ({ 
  show, 
  onHide, 
  onSave, 
  title = "Add Measure Guide",
  companyCode,
  styleNumber
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    styleNumber: styleNumber || '',
    image: null
  });
  const [existingGuide, setExistingGuide] = useState(null);
  const [existingGuidesForStyle, setExistingGuidesForStyle] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [selectedGuideId, setSelectedGuideId] = useState(null);
  const [allMeasureGuides, setAllMeasureGuides] = useState([]);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [guideToDelete, setGuideToDelete] = useState(null);

  // Placeholder image data URL
  const placeholderImage = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="120"><rect width="100%" height="100%" fill="%23f8f9fa"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%23999" font-size="14">No image</text></svg>';

  // Fetch all measure guides when modal opens
  useEffect(() => {
    const fetchAllGuides = async () => {
      if (show && companyCode) {
        try {
          const response = await fetch(`${BASE_URL}/api/admin/measure-guides?company_code=${companyCode}`, {
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          });
          const data = await response.json();
          if (data.success) {
            setAllMeasureGuides(data.measure_guides || []);
          } else {
            setAllMeasureGuides([]);
          }
        } catch (err) {
          setAllMeasureGuides([]);
        }
      } else {
        setAllMeasureGuides([]);
      }
    };
    fetchAllGuides();
  }, [show, companyCode]);

  // When guides are loaded (or styleNumber changes), compute any guides that belong to the current style
  useEffect(() => {
    if (show && styleNumber && allMeasureGuides && allMeasureGuides.length > 0) {
      const matches = allMeasureGuides.filter((guide) => {
        // Check company code
        if (guide.company_code !== companyCode) return false;
        
        const gnum = guide.style_number || guide.styleNumber || guide.style_no || guide.styleNo || guide.style;
        if (!gnum) return false;
        return String(gnum).toLowerCase() === String(styleNumber).toLowerCase();
      });
      setExistingGuidesForStyle(matches);
    } else {
      setExistingGuidesForStyle([]);
    }
  }, [show, styleNumber, allMeasureGuides, companyCode]);

  // Delete measure guide handler
  const handleDeleteGuide = async (guideId, event) => {
    // Prevent event bubbling to avoid triggering the guide item click
    event.stopPropagation();
    
    // Open confirm modal 
    setGuideToDelete(guideId);
    setShowConfirmDelete(true);
  };

  // Confirm deletion
  const confirmDeleteGuide = async () => {
    if (!guideToDelete) return;
    
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${BASE_URL}/api/admin/measure-guides/${guideToDelete}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Measure guide deleted successfully!');
        
        // Remove the deleted guide from the list
        setAllMeasureGuides(prevGuides => 
          prevGuides.filter(guide => 
            guide.id !== guideToDelete && guide.measure_guide_id !== guideToDelete
          )
        );
        
        // If deleted guide is the one currently shown as existing, clear it
        if (existingGuide && (existingGuide.id === guideToDelete || existingGuide.measure_guide_id === guideToDelete)) {
          setExistingGuide(null);
        }
        // Remove success label after 1.5s
        setTimeout(() => setSuccess(''), 1500);
      } else {
        setError(data.message || 'Failed to delete measure guide');
      }
    } catch (err) {
      setError('Error deleting measure guide: ' + err.message);
    } finally {
      setLoading(false);
      setShowConfirmDelete(false);
      setGuideToDelete(null);
    }
  };

  // Handle guide item click to show image
  const handleGuideItemClick = (guide) => {
    // Compute a robust URL for several possible backend properties
    const computed = getDisplayImageUrl(guide);
    if (computed) {
      setPreviewImage(computed);
    } else {
      setPreviewImage(placeholderImage);
    }
  };

  // Helper to compute a usable image URL from various backend fields
  const getDisplayImageUrl = (guide) => {
    if (!guide) return null;
    if (guide.full_image_url) return guide.full_image_url.startsWith('http') ? guide.full_image_url : `${BASE_URL}${guide.full_image_url}`;
    if (guide.image_url) return guide.image_url.startsWith('http') ? guide.image_url : `${BASE_URL}${guide.image_url}`;
    if (guide.image_path) return guide.image_path.startsWith('http') ? guide.image_path : `${BASE_URL}/uploads/measure-guides/${guide.image_path}`;
    if (guide.image) return guide.image.startsWith('http') ? guide.image : `${BASE_URL}/uploads/measure-guides/${guide.image}`;
    return null;
  };

  // Helper to extract a filename
  const getImageFileName = (guide, idx) => {
    if (!guide) return `Guide ${typeof idx === 'number' ? idx + 1 : ''}`;

    const extractFilenameFromPath = (p) => {
      if (!p) return null;
      try {
        // If it's a URL, parse pathname
        if (/^https?:\/\//i.test(p)) {
          const url = new URL(p);
          const seg = url.pathname.split('/').filter(Boolean).pop();
          return seg ? decodeURIComponent(seg) : url.pathname;
        }
        // Otherwise split by slash and return last segment
        const parts = p.split('/').filter(Boolean);
        return parts.length ? parts.pop() : p;
      } catch (e) {
        // Fallback: return the raw string
        return p;
      }
    };

    const candidates = [guide.image, guide.image_path, guide.image_url, guide.full_image_url];
    for (let c of candidates) {
      if (!c) continue;
      const name = extractFilenameFromPath(c);
      if (name) return name;
    }

    // Fallback to any name fields, otherwise a numbered label
    return guide.name || guide.style_name || `Guide ${typeof idx === 'number' ? idx + 1 : ''}`;
  };

  // Fetch main categories when modal opens
  useEffect(() => {
    if (show && companyCode) {
      fetchMainCategories();
    }
  }, [show, companyCode]);

  // Fetch main categories from backend
  const fetchMainCategories = async () => {
    try {
      await fetch(`${BASE_URL}/api/admin/categories/get-categories?company_code=${companyCode}`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
    } catch (err) {
      setError('Error fetching categories: ' + err.message);
    }
    fetchMainCategories();
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (show) {
      setError('');
      setSuccess('');
      setFormData({
        image: null,
        styleNumber: styleNumber || ''
      });
      setPreviewImage(null); // Reset preview image when modal opens
      // revoke any previous object URL
      if (filePreview) {
        URL.revokeObjectURL(filePreview);
        setFilePreview(null);
      }
  setSelectedGuideId(null);
    }
  }, [show, styleNumber]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({
      ...prev,
      image: file
    }));
    if (file) {
      // revoke previous
      if (filePreview) URL.revokeObjectURL(filePreview);
      const objUrl = URL.createObjectURL(file);
      setFilePreview(objUrl);
    } else {
      if (filePreview) {
        URL.revokeObjectURL(filePreview);
      }
      setFilePreview(null);
    }
  };

  // Cleanup object URL when filePreview changes or component unmounts
  useEffect(() => {
    return () => {
      if (filePreview) {
        try { URL.revokeObjectURL(filePreview); } catch (err) { }
      }
    };
  }, [filePreview]);

  const handleSave = async () => {
    // If neither a new image nor an existing guide is selected, error
    if (!formData.image && !selectedGuideId) {
      setError('Please select an image or choose an existing guide');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('company_code', companyCode);
      formDataToSend.append('style_number', formData.styleNumber);
      // If user selected an existing guide, send its id instead of uploading a file
      if (selectedGuideId) {
        formDataToSend.append('existing_guide_id', selectedGuideId);
      } else if (formData.image) {
        formDataToSend.append('image', formData.image);
      }

      const response = await fetch(`${BASE_URL}/api/admin/measure-guides`, {
        method: 'POST',
        body: formDataToSend
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Measure guide added successfully!');
        // Refresh the guides list
        const updatedResponse = await fetch(`${BASE_URL}/api/admin/measure-guides?company_code=${companyCode}`, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        const updatedData = await updatedResponse.json();
        if (updatedData.success) {
          setAllMeasureGuides(updatedData.measure_guides || []);
        }
        
        if (onSave) {
          onSave('Measure guide added successfully!');
        }
        setTimeout(() => {
          onHide();
        }, 25);
      } else {
        setError(data.message || 'Failed to save measure guide');
      }
    } catch (err) {
      setError('Error saving measure guide: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="lg" 
      centered
      backdrop="static"
      className='measure-guide-modal'
    >
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {/* Show existing guide names for this style when available */}
        {existingGuidesForStyle && existingGuidesForStyle.length > 0 && (
          <div className="mb-3">
            Existing measure guide for this product:
            <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {existingGuidesForStyle.map((g, idx) => {
                const displayImageUrl = getDisplayImageUrl(g) || placeholderImage;
                return (
                  <div 
                    key={g.id || g.measure_guide_id} 
                    style={{ 
                      display: 'flex', 
                      flexDirection: 'row', 
                      gap: 12, 
                      alignItems: 'center',
                      padding: 8,
                      border: '1px solid #dee2e6',
                      borderRadius: 8,
                      backgroundColor: '#fff',
                      width: '100%'
                    }}
                  >
                    <div style={{ width: '40%', flexShrink: 0 }}>
                      <img
                        src={displayImageUrl}
                        alt={getImageFileName(g, idx)}
                        className="mg-image"
                        style={{ 
                          width: '100%', 
                          height: 80, 
                          objectFit: 'cover', 
                          borderRadius: 4,
                          cursor: 'pointer'
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleGuideItemClick(g);
                        }}
                        onError={(e) => {
                          console.error('Image failed to load:', displayImageUrl);
                          e.target.src = placeholderImage;
                          e.target.style.objectFit = 'contain';
                        }}
                      />
                    </div>
                    <div style={{ width: '40%', fontSize: '13px', wordBreak: 'break-word' }}>
                      {getImageFileName(g, idx)}
                    </div>
                    <div style={{ width: '20%', display: 'flex', justifyContent: 'center' }}>
                      <Button
                        className="mg-remove-btn"
                        variant="danger"
                        size="sm"
                        title="Remove this measure guide"
                        disabled={loading}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const id = g.id || g.measure_guide_id;
                          handleDeleteGuide(id, e);
                        }}
                        style={{ cursor: 'pointer', fontSize: '0.85rem' }}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}         
        <Form>
          <Form.Group className="mb-3">
            <Form.Label> Add Measure Guide Image</Form.Label>
            <Form.Control
              type="file"
              onChange={handleFileChange}
              accept="image/*"
              required
            />

            {/* Thumbnail preview for newly selected file */}
            {filePreview && (
              <div className="mt-2 mb-3">
                <img
                  src={filePreview}
                  alt="Selected measure guide"
                  className="mg-image mg-thumbnail"
                  style={{ cursor: 'pointer', maxWidth: '200px', display: 'block' }}
                  onClick={() => setPreviewImage(filePreview)}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
          </Form.Group>
        </Form>
        
        {/* Display all existing measure guides */}
        {allMeasureGuides.length > 0 && (
          <div className="mb-4">
            <h5>Existing Measure Guides</h5>
            <div className="existing-measure-guides">
              {allMeasureGuides
                .filter(guide => guide.company_code === companyCode)
                .map((guide) => {
                const displayImageUrl = getDisplayImageUrl(guide) || placeholderImage;

                return (
                  <div
                    key={guide.measure_guide_id || guide.id}
                    className={`existing-guide-items ${selectedGuideId === (guide.id || guide.measure_guide_id) ? 'existing-guide-selected' : ''}`}
                    title="Click to view full image"
                  >
                    <img
                      src={displayImageUrl}
                      className='mg-image'
                      alt={guide.style_name || 'measure guide'}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleGuideItemClick(guide);
                      }}
                      onError={(e) => {
                        console.error('Image failed to load:', displayImageUrl);
                        e.target.src = placeholderImage;
                        e.target.style.objectFit = 'contain';
                      }}
                    />
                    <div className='style'>
                      {guide.style_name || guide.name || getImageFileName(guide)}
                    </div>

                    <div className="mg-action-group">
                      <button
                        type="button"
                        className='mg-use-btn'
                        title="Use this measure guide"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const id = guide.id || guide.measure_guide_id;
                          setSelectedGuideId(id);
                          setSuccess('Selected measure guide for use');
                          setTimeout(() => setSuccess(''), 1500);
                        }}
                      >
                        Use
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Image Preview Modal */}
        <ImagePreviewModal 
          show={!!previewImage}
          onHide={() => setPreviewImage(null)}
          previewImage={previewImage}
          setError={setError}
        />

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          show={showConfirmDelete}
          onHide={() => {
            setShowConfirmDelete(false);
            setGuideToDelete(null);
          }}
          onConfirm={confirmDeleteGuide}
          title="Delete Measure Guide"
          message="Are you sure you want to delete this measure guide? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          confirmVariant="danger"
          loading={loading}
        />
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSave} 
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Measure Guide'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MeasureGuideModal;